import { defineStore } from 'pinia'
import { client } from '@/services/client'
import { useSpace } from '@/store/modules/App/space'
import type { Pipeline, PipelineResponse } from '@/types'

type State = {
  pipelineId: number | null
  pipelines: Pipeline[]
}

export const usePipelinesStore = defineStore('pipelines', {
  state: (): State => {
    return {
      pipelineId: null,
      pipelines: [],
    }
  },
  actions: {
    updatePipelineId(value: number | null): void {
      if (value !== 0 && value !== null && value !== undefined) {
        this.pipelineId = value
        return
      }

      this.pipelineId = null
    },

    setPipelines(pipelines: Pipeline[]): void {
      this.pipelines = pipelines
    },

    clearPipelines(): void {
      this.pipelines = []
    },

    setNewPipeline(pipeline: Pipeline): void {
      this.pipelines.push(pipeline)
    },

    updatePipelineOnState(pipeline: Pipeline): void {
      this.pipelines = this.pipelines.map((item) => {
        if (item.id === pipeline.id) {
          return {
            ...item,
            ...pipeline,
          }
        }
        return item
      })
    },

    async loadPipelines(spaceId: string): Promise<Pipeline[]> {
      const { data } = await client.get(`/spaces/${spaceId}/branches/`, {
        region: useSpace().region,
      })

      this.setPipelines(data.branches)

      return data.branches
    },

    async createPipeline(payload: {
      spaceId: string
      pipeline: PipelineResponse
    }): Promise<Pipeline> {
      const { spaceId, pipeline } = payload

      const { data } = await client.post(
        `spaces/${spaceId}/branches/`,
        pipeline,
        {
          region: useSpace().region,
        },
      )

      this.setNewPipeline(data.branch)

      return data.branch
    },

    async deletePipeline(payload: {
      spaceId: string
      pipelineId: number
    }): Promise<void> {
      const { spaceId, pipelineId } = payload

      await client.delete(`spaces/${spaceId}/branches/${pipelineId}`, {
        region: useSpace().region,
      })

      await this.loadPipelines(spaceId)
    },

    async updatePipeline(payload: {
      spaceId: string
      pipeline: PipelineResponse
      id: number
    }): Promise<void> {
      const { spaceId, pipeline, id } = payload

      await client.put(`spaces/${spaceId}/branches/${id}`, pipeline, {
        region: useSpace().region,
      })

      this.updatePipelineOnState(pipeline.branch)
    },

    async reorderPipeline(payload: {
      spaceId: string
      pipeline: PipelineResponse
      id: number
    }): Promise<void> {
      const { spaceId, pipeline, id } = payload

      await client.put(`spaces/${spaceId}/branches/${id}`, pipeline, {
        region: useSpace().region,
      })

      this.loadPipelines(spaceId)
    },

    async deployPipeline({
      spaceId,
      payload,
    }: {
      spaceId: string
      payload: { branch_id: string; release_uuids: string[] }
    }): Promise<void> {
      await client.post(
        `spaces/${spaceId}/deployments/`,
        {
          ...payload,
        },
        {
          region: useSpace().region,
        },
      )
    },
  },
  getters: {
    getPipelineId: (state: State): number | null => state.pipelineId ?? null,
    getPipelines: (state: State): Pipeline[] => state.pipelines || [],
    getPipelineById:
      (state: State) =>
      (pipelineId: number): Pipeline | undefined =>
        state.pipelines.find(
          (pipeline: Pipeline) => pipeline.id === pipelineId,
        ),
  },
})
